import { Checkbox, Chip, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useWindowDimensions } from "../components/WindowDimensions";
import { FormatDate, IsNotEmpty } from "../Helpers";
import { I18N } from "../I18N";
import { DevelopmentItem } from "../models/dtos";
import DevelopmentItemCompletePopup from "../popups/DevelopmentItemCompletePopup";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import ConditionalDisplay from "../components/ConditionalDisplay";
import DevelopmentClient from "../apiClients/DevelopmentClient";
import MobileEntityCard from "../components/responsive/MobileEntityCard";
import ResponsiveCollection from "../components/responsive/ResponsiveCollection";
import useViewportMode from "../hooks/useViewportMode";

export interface DataGridProItem {
    cannotBeSelected: boolean;
    isSelected: boolean
}

export interface DevelopmentItemGridItem extends DevelopmentItem, DataGridProItem { };

export interface DevelopmentItemSelectionProps {
    data: DevelopmentItem[];
    instructions: string;
    selectedIds?: string[];
    onSelectionChange: (ids: string[]) => Promise<void> | void;
}

const DevelopmentItemSelectionComponent = ({ data, instructions, selectedIds, onSelectionChange }: DevelopmentItemSelectionProps) => {

    const { height } = useWindowDimensions();
    const viewport = useViewportMode();
    const [gridItems, setGridItems] = useState<DevelopmentItem[]>([]);

    useEffect(() => {

        if (data.length > 0) {
            setGridItems([...data]);
            const tempIds: string[] = [];

            data.forEach(di => {
                const devItemMustBeSelected = (dayjs(di.StartDate) > dayjs());

                if (devItemMustBeSelected) {
                    tempIds.push(di.Id);
                }

            });

            if (tempIds.length > 0) {
                onSelectionChange(tempIds);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);



    const handleRowClicked = (ids: string[]) => {
        onSelectionChange(ids);
    }

    const toggleSelection = (item: DevelopmentItem) => {
        if (!handleRowSelectable(item))
            return;

        const currentIds = selectedIds ?? [];
        handleRowClicked(currentIds.includes(item.Id)
            ? currentIds.filter(id => id !== item.Id)
            : [...currentIds, item.Id]);
    }

    const handleRowSelectable = (row): boolean => {
        let isSelectable = true;
        if (!DevelopmentClient.RuleStartDateBeforeToday(row.StartDate))
            isSelectable = false;
        if (DevelopmentClient.RuleItemCompleted(row.CompleteDate))
            isSelectable = false;

        return isSelectable;
    };

    const handleSubmit = () => {
        gridItems[0].CompleteDate = dayjs().toISOString();
        setGridItems([...gridItems]);
        onSelectionChange(selectedIds);
    }

    const renderDevelopmentItemCard = (item: DevelopmentItem) => {
        const isCompleted = DevelopmentClient.RuleItemCompleted(item.CompleteDate);
        const isSelectable = handleRowSelectable(item);

        return (
            <MobileEntityCard
                ariaLabel={`Development item: ${item.Title}`}
                title={item.Title}
                subtitle={item.Narrative}
                status={isCompleted
                    ? <Chip size="small" color="success" variant="outlined" label="Completed" />
                    : <Checkbox
                        checked={(selectedIds ?? []).includes(item.Id)}
                        disabled={!isSelectable}
                        onChange={() => toggleSelection(item)}
                        inputProps={{ 'aria-label': `Select development item: ${item.Title}` }}
                        sx={{ width: 44, height: 44 }}
                    />}
                fields={[
                    { label: I18N.Translate('Start Date'), value: FormatDate(item.StartDate), fullWidth: true },
                ]}
                actions={DevelopmentClient.RuleStartDateBeforeToday(item.StartDate)
                    ? <DevelopmentItemCompletePopup
                        developmentItem={item}
                        onSubmit={handleSubmit}
                        disabled={isCompleted}
                        aria-label={`${isCompleted ? 'Completed' : 'Complete'} development item: ${item.Title}`}
                        icon={isCompleted
                            ? <CheckCircleOutlineIcon fontSize="small" htmlColor="green" />
                            : <AddTaskIcon fontSize="small" htmlColor="red" />}
                    />
                    : undefined}
            />
        );
    };

    const desktopGrid = (
        <div style={{ height: height - 400, width: '100%' }}>
            <DataGridPro
                density="compact"
                checkboxSelection={true}
                disableSelectionOnClick={true}
                onSelectionModelChange={handleRowClicked}
                selectionModel={selectedIds}
                rows={gridItems.map(x => { return { ...x, id: x.Id } })}
                hideFooter={true}
                isRowSelectable={(params) => handleRowSelectable(params.row)}
                columns={[
                    {
                        field: 'Action',
                        type: 'actions',
                        headerName: "Complete(d)",
                        sortable: false,
                        filterable: false,
                        resizable: false,
                        width: 100,
                        align: 'center',
                        disableColumnMenu: true,
                        disableReorder: true,

                        renderCell: ({ row: r }) =>
                            <ConditionalDisplay ifTrue={DevelopmentClient.RuleStartDateBeforeToday(r.StartDate)}>
                                <DevelopmentItemCompletePopup
                                    developmentItem={r}
                                    onSubmit={handleSubmit}
                                    disabled={DevelopmentClient.RuleItemCompleted(r.CompleteDate)}
                                    icon={(IsNotEmpty(r.CompleteDate)) ? <CheckCircleOutlineIcon fontSize='small' htmlColor='green' /> : <AddTaskIcon fontSize='small' htmlColor='red' />} />
                            </ConditionalDisplay>
                    },
                    {
                        field: 'Name',
                        headerName: I18N.Translate('Title'),
                        width: 250,
                        renderCell: ({ row: r }) => r.Title
                    },
                    {
                        field: 'Narrative',
                        headerName: I18N.Translate('Narrative'),
                        flex: 1,
                        renderCell: ({ row: r }) => r.Narrative.substring(0, 100)
                    },
                    {
                        field: 'StartDate',
                        headerName: I18N.Translate('Start Date'),
                        width: 120,
                        renderCell: ({ row: r }) => FormatDate(r.StartDate)
                    },
                ]}
            />
        </div>
    );

    return (
        <>
            <Typography sx={{ marginLeft: '1em', marginBottom: '.5em' }}>{instructions}</Typography>
            <ResponsiveCollection
                viewMode={viewport.isTablet && viewport.isPortrait ? 'phone' : viewport.mode}
                items={gridItems}
                getKey={item => item.Id}
                renderCard={renderDevelopmentItemCard}
                desktop={desktopGrid}
                ariaLabel="Development plan items"
                emptyState={<Typography sx={{ p: 2 }} color="text.secondary">No development items available</Typography>}
            />
        </>
    );
}

export default DevelopmentItemSelectionComponent;
